// TranscriptQA - Q&A interface for transcripts
import React from 'react';
import { Sparkles, Send, Copy, Download, Edit2, FileText, Search, Loader2 } from 'lucide-react';
import { buttonStyles, containerStyles } from './buttonStyles';

interface QAPair {
  question: string;
  answer: string;
}

interface TranscriptQAProps {
  // Transcript data
  transcript: string;
  transcriptSegments: Array<{start: number, end: number, text: string}>;
  showTimestamps: boolean;
  
  // Q&A state
  qaPairs: QAPair[];
  currentQuestion: string;
  questionTemplate: string;
  showTemplateEdit: boolean;
  isGenerating: boolean;
  editingIndex: number | null;
  editedQuestion: string;
  searchQuery: string;
  
  // Handlers
  onSetCurrentQuestion: (value: string) => void;
  onSetQuestionTemplate: (value: string) => void;
  onSetShowTemplateEdit: (value: boolean) => void;
  onSetEditingIndex: (index: number | null) => void;
  onSetEditedQuestion: (value: string) => void;
  onSetSearchQuery: (value: string) => void;
  onSetShowTimestamps: (value: boolean) => void;
  onGetAnswer: () => void;
  onRegenerateAnswer: (index: number) => void;
  onCopyAll: () => void;
  onDownload: () => void;
  
  // Utilities
  getFormattedTranscript: () => string;
}

export const TranscriptQA: React.FC<TranscriptQAProps> = ({
  transcript,
  transcriptSegments,
  showTimestamps,
  qaPairs,
  currentQuestion,
  questionTemplate,
  showTemplateEdit,
  isGenerating,
  editingIndex,
  editedQuestion,
  searchQuery,
  onSetCurrentQuestion,
  onSetQuestionTemplate,
  onSetShowTemplateEdit,
  onSetEditingIndex,
  onSetEditedQuestion,
  onSetSearchQuery,
  onSetShowTimestamps,
  onGetAnswer,
  onRegenerateAnswer,
  onCopyAll,
  onDownload,
  getFormattedTranscript,
}) => {
  // Filter Q&A pairs by search query
  const filteredQAPairs = React.useMemo(() => {
    if (!searchQuery.trim()) return qaPairs;
    const query = searchQuery.toLowerCase();
    return qaPairs.filter((qa, idx) =>
      qa.question.toLowerCase().includes(query) ||
      qa.answer.toLowerCase().includes(query) ||
      `q${idx + 1}`.includes(query)
    );
  }, [qaPairs, searchQuery]);

  return (
    <div className={containerStyles.qaSection}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-amber-500/20">
        <h3 className="text-base md:text-lg font-bold text-amber-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-amber-400 animate-pulse" />
          Free Q&A Workspace
        </h3>
        <div className="flex gap-2">
          {qaPairs.length > 0 && (
            <>
              <button onClick={onCopyAll} className={buttonStyles.copyAll}>
                <Copy className="w-3 h-3 md:w-3.5 md:h-3.5" />
                <span className="hidden sm:inline">Copy All</span>
                <span className="sm:hidden">Copy</span>
              </button>
              <button onClick={onDownload} className={buttonStyles.download}>
                <Download className="w-3 h-3 md:w-3.5 md:h-3.5" />
                <span className="hidden sm:inline">Download</span>
                <span className="sm:hidden">Save</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Collapsible Transcript Viewer */}
      <details className="rounded-xl border border-blue-500/30 bg-slate-950/50 overflow-hidden">
        <summary className="px-3 md:px-4 py-2.5 md:py-3 bg-blue-900/20 hover:bg-blue-900/30 cursor-pointer text-blue-100 text-xs md:text-sm font-semibold flex items-center gap-2 transition-colors">
          <FileText className="w-3.5 h-3.5 md:w-4 md:h-4" />
          <span className="flex-1">View Full Transcript {transcriptSegments.length > 0 && `(${transcriptSegments.length})`}</span>
        </summary>
        <div className="p-3 md:p-4 space-y-3 max-h-60 md:max-h-80 overflow-y-auto">
          {transcriptSegments.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between pb-2 border-b border-blue-500/20">
                <p className="text-xs text-blue-300">Transcript with timestamps</p>
                <button
                  onClick={() => onSetShowTimestamps(!showTimestamps)}
                  className="px-2 py-1 rounded bg-blue-600/20 hover:bg-blue-600/30 text-blue-200 text-xs"
                >
                  {showTimestamps ? 'Hide' : 'Show'} Times
                </button>
              </div>
              <pre className="text-xs md:text-sm text-slate-300 whitespace-pre-wrap font-sans break-words">
                {showTimestamps ? getFormattedTranscript() : transcript}
              </pre>
            </div>
          ) : (
            <pre className="text-xs md:text-sm text-slate-300 whitespace-pre-wrap font-sans break-words">
              {transcript}
            </pre>
          )}
        </div>
      </details>

      {/* Q&A History - 3 Column Layout */}
      {qaPairs.length > 0 && (
        <div className="space-y-3">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400" />
            <input
              type="text"
              placeholder="Search questions and answers..."
              value={searchQuery}
              onChange={(e) => onSetSearchQuery(e.target.value)}
              className={containerStyles.searchInput}
            />
            {searchQuery && (
              <button
                onClick={() => onSetSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 hover:text-amber-300 text-xs"
              >
                Clear
              </button>
            )}
          </div>
          
          {/* Results count */}
          {searchQuery && (
            <p className="text-xs text-amber-300/70">
              Found {filteredQAPairs.length} of {qaPairs.length} results
            </p>
          )}
          
          {/* 2-Column Grid: Each Q&A pair in its own row */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {filteredQAPairs.map((qa, idx) => {
              const originalIdx = qaPairs.findIndex(item => item === qa);
              
              return (
                <div key={`qa-pair-${originalIdx}`} className="grid grid-cols-2 gap-4">
                  {/* LEFT: ANSWER */}
                  <div className="bg-slate-800/40 rounded-lg border border-amber-500/20 p-3">
                    <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      A{originalIdx + 1}
                    </p>
                    <p className="text-xs md:text-sm text-amber-100/90 leading-relaxed whitespace-pre-wrap break-words">
                      {qa.answer}
                    </p>
                  </div>

                  {/* RIGHT: QUESTION */}
                  <div className="bg-slate-800/40 rounded-lg border border-amber-500/20 p-3 group">
                    {editingIndex === originalIdx ? (
                      <div className="space-y-2">
                        <textarea
                          value={editedQuestion}
                          onChange={(e) => onSetEditedQuestion(e.target.value)}
                          className="w-full rounded-lg bg-slate-950/70 border border-amber-500/50 text-amber-100 p-2 text-xs md:text-sm h-20 focus:outline-none focus:border-amber-400"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => onRegenerateAnswer(originalIdx)}
                            disabled={isGenerating}
                            className="flex-1 px-2 py-1.5 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 text-white text-xs font-semibold disabled:opacity-50"
                          >
                            {isGenerating ? 'Regenerating...' : 'Regenerate'}
                          </button>
                          <button
                            onClick={() => {
                              onSetEditingIndex(null);
                              onSetEditedQuestion('');
                            }}
                            className="px-2 py-1.5 rounded-lg bg-slate-700 text-slate-300 text-xs"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-amber-400 mb-1 flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5" />
                              Q{originalIdx + 1}
                            </p>
                            <p className="text-xs md:text-sm text-amber-100/90 leading-relaxed break-words">
                              {qa.question}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              onSetEditingIndex(originalIdx);
                              onSetEditedQuestion(qa.question);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-amber-700/30 rounded flex-shrink-0 transition-opacity"
                            title="Edit & Regenerate"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-amber-400" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Question Template Section */}
      <div className="space-y-2 pt-4 border-t border-amber-500/20">
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
            Default Instructions (Optional)
          </label>
          <button
            onClick={() => onSetShowTemplateEdit(!showTemplateEdit)}
            className="text-xs text-amber-400 hover:text-amber-300 underline"
          >
            {showTemplateEdit ? 'Hide' : 'Edit Template'}
          </button>
        </div>
        
        {showTemplateEdit && (
          <div className="space-y-2">
            <textarea
              className="w-full rounded-lg bg-slate-900/50 border border-amber-500/30 text-amber-100 p-2 text-xs h-20 focus:outline-none focus:border-amber-400 placeholder-amber-400/20"
              placeholder="These instructions will be automatically added to every question..."
              value={questionTemplate}
              onChange={(e) => onSetQuestionTemplate(e.target.value)}
            />
            <p className="text-[10px] text-amber-400/60 italic">
              💡 This template is automatically appended to every question you ask
            </p>
          </div>
        )}
        
        {!showTemplateEdit && questionTemplate && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
            <p className="text-[10px] text-amber-300/70 line-clamp-2">
              ✓ Template active: {questionTemplate}
            </p>
          </div>
        )}
      </div>

      {/* New Question Input */}
      <div className="space-y-3 pt-4 border-t border-amber-500/20">
        <label className="text-sm font-semibold text-amber-200">
          Question #{qaPairs.length + 1}:
        </label>
        <textarea
          className={containerStyles.textareaQA}
          placeholder="Type your question here..."
          value={currentQuestion}
          onChange={(e) => onSetCurrentQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.ctrlKey && currentQuestion.trim()) {
              e.preventDefault();
              onGetAnswer();
            }
          }}
        />
        <button
          onClick={onGetAnswer}
          disabled={!currentQuestion.trim() || isGenerating}
          className={buttonStyles.primaryLarge}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Answer...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Get Answer (Ctrl+Enter)
            </>
          )}
        </button>
      </div>
    </div>
  );
};
